import type { ColorConstructorWithOklch } from 'color'
import Immutable from 'immutable'
import color from 'color'
import postcss from 'postcss'
import postcssContrastColorFunction from '@csstools/postcss-contrast-color-function'
import valueParser from 'postcss-value-parser'

// Note: Keep in sync with fusion-backend-ts/src/utils/themes
export const themeGroupDelimiter = ':'

/**
 * Gets the path to a stylesheet for given theme via `themeName`.
 * Defaults to the main stylesheet path when `fileNameSuffix` is not given.
 * Example file name suffix: "-ep6" for the theme's checkout and customer area stylesheet.
 *
 * Note: Keep in sync with fusion-backend-ts/src/utils/themes
 *
 * @param themeName - The theme name (e.g., "epages.dimension:bodycare")
 * @param hashedThemeAssets - Map of hashed asset names from webpack stats
 * @param fileNameSuffix - Optional suffix for the filename (e.g., "-ep6")
 */
export function getThemeStylesheetPath(
  themeName: string,
  hashedThemeAssets: ImmutableMap,
  fileNameSuffix = '',
): string {
  // File name convention:
  // If it's a standalone theme, default file name to "style".
  // If it's a theme inside a theme group directory, look for a file with its scoped name.
  const [directoryName, fileName = 'style'] = themeName.split(themeGroupDelimiter)

  const stylesheetKey = `${directoryName}/${fileName}${fileNameSuffix}`
  const hashedFileName = hashedThemeAssets.getIn(['stylesheets', stylesheetKey])

  return `/themes/${directoryName}/stylesheets/${hashedFileName}`
}

/**
 * Gets the preview image path for given theme.
 *
 * @param themeName Name of the theme to get the preview image for.
 * @param hashedThemeAssets The theme's hashed theme assets from store state.
 */
export function getThemePreviewImagePath(themeName: string, hashedThemeAssets: ImmutableMap): string {
  // File name convention:
  // If it's a standalone theme, default file name to "preview".
  // If it's a theme inside a theme group directory, look for a file with its scoped name.
  const [directoryName, fileName = 'preview'] = themeName.split(themeGroupDelimiter)
  const hashedFileName = hashedThemeAssets.getIn([`${directoryName}/img`, `${fileName}.jpg`])

  return `/themes/${directoryName}/img/${hashedFileName}`
}

/**
 * Mapping of old header type names with their name.
 *
 * This is needed until the old header type names are no longer in use
 * (i.e. not in the database of any platform).
 */
const replacementHeaderTypes: Record<string, string> = {
  neutral: 'threeRows',
  vision: 'oneRowLogoLeft',
  twoRowsNestedMenu: 'twoRowsLogoLeft',
  uptown: 'twoRowsLogoLeft',
  editorial: 'twoRowsLogoLeft',
  essence: 'twoRowsLogoLeft',
  structure: 'twoRowsLogoLeft',
  limelight: 'twoRowsLogoLeft',
}

/**
 * Mapping of new color keys to old color keys by theme.
 *
 * This is needed until the old color keys are no longer in use
 * (i.e. not in the database of any platform).
 *
 * When the schema uses new keys but old shops have old keys in the database,
 * this mapping allows us to look up the old values.
 * Structure: { [themeName]: { [newKey]: oldKey } }
 */
const oldColorKeysByTheme: Record<string, Record<string, string>> = {
  'epages.dimension:bodycare': {
    color1: 'primaryColor',
    color2: 'backgroundColor',
  },
  'epages.dimension:coffeeshop': {
    color1: 'primaryColor',
    color2: 'backgroundColor',
  },
  'epages.dimension:countryclub': {
    color1: 'primaryColor',
    color2: 'backgroundColor',
  },
  'epages.editorial:home-and-living': {
    color1: 'accentColor',
    color2: 'primaryColor',
  },
  'epages.editorial:modern': {
    color1: 'accentColor',
    color2: 'primaryColor',
  },
  'epages.editorial:sportswear': {
    color1: 'accentColor',
    color2: 'primaryColor',
  },
  'epages.editorial:vintage': {
    color1: 'accentColor',
    color2: 'primaryColor',
  },
  'epages.editorial:vivid': {
    color1: 'accentColor',
    color2: 'primaryColor',
  },
  'epages.essence:classic': {
    color1: 'accentColor',
    color2: 'borderColor',
    color3: 'footerBackground',
  },
  'epages.essence:fresh': {
    color1: 'accentColor',
    color2: 'borderColor',
    color3: 'footerBackground',
  },
  'epages.essence:pure': {
    color1: 'accentColor',
    color2: 'borderColor',
    color3: 'footerBackground',
  },
  'epages.limelight:bold': {
    color1: 'primaryColor',
    color2: 'headerBackground',
    color3: 'footerColor',
    footerContent: 'footerColor',
  },
  'epages.limelight:current': {
    color1: 'primaryColor',
    color2: 'headerBackground',
    color3: 'footerColor',
    footerContent: 'footerColor',
  },
  'epages.limelight:harmonic': {
    color1: 'primaryColor',
    color2: 'headerBackground',
    color3: 'footerColor',
    footerContent: 'footerColor',
  },
  'epages.neutral:basic': {
    color1: 'accentColor',
    color2: 'backgroundColor',
    color3: 'primaryColor',
  },
  'epages.neutral:light': {
    color1: 'accentColor',
    color2: 'backgroundColor',
    color3: 'primaryColor',
  },
  'epages.neutral:solid': {
    color1: 'accentColor',
    color2: 'backgroundColor',
    color3: 'primaryColor',
  },
  'epages.neutral:toys': {
    color1: 'accentColor',
    color2: 'backgroundColor',
    color3: 'primaryColor',
  },
  'epages.structure:contemporary': {
    color1: 'accentColor',
    color2: 'backgroundColor',
    color3: 'primaryColor',
    color4: 'footerColor',
    footerContent: 'footerColor',
  },
  'epages.structure:individual': {
    color1: 'accentColor',
    color2: 'backgroundColor',
    color3: 'primaryColor',
    color4: 'footerColor',
    footerContent: 'footerColor',
  },
  'epages.structure:prime': {
    color1: 'accentColor',
    color2: 'backgroundColor',
    color3: 'primaryColor',
    color4: 'footerColor',
    footerContent: 'footerColor',
  },
  'epages.uptown:cool': {
    color1: 'accentColor',
    color2: 'backgroundColor',
    color3: 'primaryColor',
  },
  'epages.uptown:deep': {
    color1: 'accentColor',
    color2: 'backgroundColor',
    color3: 'primaryColor',
  },
  'epages.uptown:sweet': {
    color1: 'accentColor',
    color2: 'backgroundColor',
    color3: 'primaryColor',
  },
  'epages.vision:elegant': {
    color1: 'accentColor',
    color2: 'headerBackground',
    color3: 'basicColor',
    color4: 'headerContent',
    headerContent: 'headerContent',
    // The old `headerContent` key also colored the menu content
    menuContent: 'headerContent',
  },
  'epages.vision:glossy': {
    color1: 'accentColor',
    color2: 'headerBackground',
    color3: 'basicColor',
    color4: 'headerContent',
    headerContent: 'headerContent',
    menuContent: 'headerContent',
  },
  'epages.vision:young': {
    color1: 'accentColor',
    color2: 'headerBackground',
    color3: 'basicColor',
    color4: 'headerContent',
    headerContent: 'headerContent',
    menuContent: 'headerContent',
  },
}

/**
 * Gets the normalized header type name for use as class name, etc.
 *
 * This is needed until the old header type names are no longer in use
 * (i.e. not in the database of any platform).
 */
export function getNormalizedHeaderType(headerType: string): string {
  return replacementHeaderTypes[headerType] || headerType
}

/**
 * Returns a PostCSS processor that resolves all `var(--X)` references and
 * `contrast-color()` functions against the given computed theme settings.
 */
export function buildThemeCssProcessor(computedThemeSettings: Theme.Settings) {
  const flatVars = createFlatObjectWithPath(Immutable.fromJS(computedThemeSettings))
  const varResolverPlugin: postcss.Plugin = {
    postcssPlugin: 'resolve-theme-vars',
    Declaration(decl) {
      decl.value = resolveVars(decl.value, flatVars)
    },
  }

  return postcss([varResolverPlugin, postcssContrastColorFunction({ preserve: false })])
}

/** Mix at which the base is fully replaced by white or black. */
export const FULL_MIX = 100

/**
 * Mixes a base color toward white (positive mix) or black (negative mix) in
 * OKLCH, the operation of CSS `color-mix(in oklch, base, white <mix>%)`.
 * Results outside sRGB are clipped per channel, as browsers do. Returns
 * uppercase hex.
 *
 * `resolveColorVariant('#0b72a7', 33)` → `#6BA0C5`, `resolveColorVariant('#0b72a7', -100)` → `#000000`
 */
export function resolveColorVariant(baseColorValue: string, mix: number): string {
  const mixFraction = Math.min(1, Math.abs(mix) / FULL_MIX)
  // color-convert reports OKLCH lightness as 0..100, so white is 100 and black 0.
  const targetLightness = mix > 0 ? 100 : 0
  const towards = (from: number, to: number) => from + (to - from) * mixFraction

  const [lightness, chroma, hue] = color(baseColorValue).oklch().array()

  const mixedLightness = towards(lightness, targetLightness)
  // White and black are achromatic, so chroma fades out and the hue has nothing to move toward.
  const mixedChroma = towards(chroma, 0)

  return (color as ColorConstructorWithOklch).oklch([mixedLightness, mixedChroma, hue]).hex()
}

// The Monochrome bases of the advanced color settings: pure black and white,
// so their variants run from one end of the lightness range.
export const SYSTEM_COLORS = {
  _black: '#000000',
  _white: '#FFFFFF',
}
const systemColorNames = Object.keys(SYSTEM_COLORS)

export const isSystemColor = (colorName: string): boolean => systemColorNames.includes(colorName)

const systemColorNameKeys: Record<string, string> = {
  _black: 'colors.monochrome.blackColor',
  _white: 'colors.monochrome.whiteColor',
}

// Translation key of a color's display name within the `themes:settings` scope.
// Palette colors live under the color group, system colors under its monochrome group.
export const themeColorNameKey = (colorName: string): string =>
  systemColorNameKeys[colorName] ?? `colors.palette.${colorName}`

// A color ref may point to a main or additional color of the color group by
// settings path, e.g. 'colors.color3'. Returns the color name for a valid path.
export function parseColorRef(
  value: unknown,
  colorGroupSchemaItem: Theme.ColorGroupSettingsSchemaItem | undefined,
): string | undefined {
  if (typeof value !== 'string' || !colorGroupSchemaItem) return undefined
  const prefix = `${colorGroupSchemaItem.name}.`
  if (!value.startsWith(prefix)) return undefined
  const colorName = value.slice(prefix.length)
  const referencableColorNames = [...colorGroupSchemaItem.colors, ...colorGroupSchemaItem.additionalColors]
  return referencableColorNames.includes(colorName) ? colorName : undefined
}

// Resolves a color ref to a color string, or undefined for an invalid ref.
// Besides palette color paths, the neutrals 'blackColor' and 'whiteColor' are
// supported; they are the Monochrome bases.
export function resolveColorRef(
  ref: unknown,
  colorGroupSchemaItem: Theme.ColorGroupSettingsSchemaItem | undefined,
  resolvedColors: Record<string, string> | undefined,
): string | undefined {
  if (ref === 'blackColor') return SYSTEM_COLORS._black
  if (ref === 'whiteColor') return SYSTEM_COLORS._white
  const colorName = parseColorRef(ref, colorGroupSchemaItem)
  return colorName ? resolvedColors?.[colorName] : undefined
}

// Post-pass for colorRef schema settings, running after the schema walk so the
// color group is fully resolved and the walk has carried the raw ref value.
// With computeValues, the effective color (custom ?? ref) is emitted into the
// `for` setting and the ref key is omitted. Without computeValues, both keys
// pass through raw so the editor can distinguish them. Either way the paired
// color setting's 'transparent' fallback is reverted to null, so an unset
// custom color stays distinguishable from a stored one.
function resolveColorRefSettings(
  schema: Theme.SettingsSchemaItem[],
  currentSettings: Theme.Settings,
  defaultSettings: Theme.Settings,
  themeSettings: Theme.Settings,
  options: { computeValues?: boolean },
  colorGroupSchemaItem: Theme.ColorGroupSettingsSchemaItem | undefined,
  resolvedColors: Record<string, string> | undefined,
): void {
  for (const schemaItem of schema) {
    if (schemaItem.type === 'group') {
      const group = themeSettings[schemaItem.name]
      if (group) {
        resolveColorRefSettings(
          (schemaItem as Theme.GroupSettingsSchemaItem).settings,
          currentSettings[schemaItem.name] ?? defaultSettings[schemaItem.name] ?? {},
          defaultSettings[schemaItem.name] ?? {},
          group,
          options,
          colorGroupSchemaItem,
          resolvedColors,
        )
      }
    } else if (schemaItem.type === 'colorRef') {
      const colorRefSchemaItem = schemaItem as Theme.ColorRefSettingsSchemaItem
      const customValue = currentSettings[colorRefSchemaItem.for] ?? defaultSettings[colorRefSchemaItem.for] ?? null

      if (options.computeValues) {
        const refValue = currentSettings[colorRefSchemaItem.name] ?? defaultSettings[colorRefSchemaItem.name] ?? null
        themeSettings[colorRefSchemaItem.for] =
          customValue ??
          resolveColorRef(refValue, colorGroupSchemaItem, resolvedColors) ??
          getThemeSettingDefaultValueFallback('color')
      } else {
        themeSettings[colorRefSchemaItem.for] = customValue
      }
    }
  }
}

/**
 * Resolves theme settings by merging current settings with defaults based on a schema.
 *
 * @param schema - Array of theme setting schema items defining the structure and types of settings
 * @param currentSettings - The current theme settings to resolve
 * @param defaultSettings - Default theme settings to use as fallback values
 * @param themeName - Name of the theme being resolved
 * @param options - Optional configuration object
 * @param options.computeValues - Whether to resolve color variants to final colors instead of keeping their { base, mix } definitions
 * @returns Resolved theme settings with all values populated from current or default settings
 *
 * @remarks
 * Provides backward compatibility for old color key names based on theme-specific mappings.
 * For color groups, processes color definitions including their variants and system colors.
 */
export function resolveThemeSettings(
  schema: Theme.SettingsSchemaItem[],
  currentSettings: Theme.Settings,
  defaultSettings: Theme.Settings,
  themeName: string,
  options: { computeValues?: boolean } = {},
): Theme.Settings {
  const themeSettings: Theme.Settings = {}

  for (const schemaItem of schema) {
    const settingValue = currentSettings[schemaItem.name] ?? defaultSettings[schemaItem.name]

    if (schemaItem.type === 'group' || schemaItem.type === 'fontGroup') {
      themeSettings[schemaItem.name] = resolveThemeSettings(
        (schemaItem as Theme.GroupSettingsSchemaItem).settings,
        settingValue ?? {},
        defaultSettings[schemaItem.name] ?? {},
        themeName,
        options,
      )
    } else if (schemaItem.type === 'colorGroup') {
      const colorGroupSchemaItem = schemaItem as Theme.ColorGroupSettingsSchemaItem
      themeSettings[colorGroupSchemaItem.name] = {}

      const baseColorNames = [
        ...colorGroupSchemaItem.colors,
        ...colorGroupSchemaItem.additionalColors,
        ...systemColorNames,
      ]

      // Colors:
      const baseColors = {}
      for (const color of baseColorNames) {
        let baseColorValue: string | undefined = color in SYSTEM_COLORS ? SYSTEM_COLORS[color] : settingValue?.[color]

        // Compat: Support old names for color keys
        // E.g. Limelight theme footerColor (old) -> color3 (new)
        // If it's a string, then we know it's an old key color value.
        // If it's an object, then the same key is also used as variant name (e.g. headerBackground in Limelight).
        const valueForOldKey: string | Theme.ColorGroupSettingsColorVariant =
          settingValue?.[oldColorKeysByTheme[themeName]?.[color]]
        if (typeof valueForOldKey === 'string') {
          baseColorValue = valueForOldKey
        }

        baseColors[color] =
          baseColorValue ||
          defaultSettings[colorGroupSchemaItem.name]?.[color] ||
          getThemeSettingDefaultValueFallback('color')
        themeSettings[colorGroupSchemaItem.name][color] = baseColors[color]
      }

      // Color variants:
      for (const variantDefinition of colorGroupSchemaItem.variants || []) {
        let variant: Theme.ColorGroupSettingsColorVariant | undefined =
          currentSettings[colorGroupSchemaItem.name]?.[variantDefinition.name] ||
          defaultSettings[colorGroupSchemaItem.name]?.[variantDefinition.name]

        // Compat: Support new variant keys that replace old color keys
        // E.g. Limelight theme: footerContent variant (new) replaces footerColor color key (old)
        //
        // When an old color key is still present in current settings, convert it to
        // the new variant format. This allows us to migrate old shops while supporting
        // shops that have already switched to the new variant keys.
        const oldKey = oldColorKeysByTheme[themeName]?.[variantDefinition.name]
        const oldValue = currentSettings[colorGroupSchemaItem.name]?.[oldKey]
        // String-only: when variant and old key share a name (vision `headerContent`),
        // `oldValue` is the user's new-format object and must not be overwritten.
        if (oldKey && typeof oldValue === 'string') {
          // Find which base color the old key maps to via oldColorKeysByTheme.
          // E.g. Limelight: footerContent -> footerColor -> color3
          const base = Object.keys(oldColorKeysByTheme[themeName] || {}).find(
            (newKey) => oldColorKeysByTheme[themeName][newKey] === oldKey,
          )

          if (base) {
            variant = {
              base,
              mix: 0,
            }
          }
        }

        // Compat
        // E.g. headerBackground in Limelight theme was previously a color key
        // fallback for compat: headerBackground case where variant name headerBackground
        // was a previous color key. In this case, `variant` is a string.
        if (typeof variant === 'string') {
          variant = defaultSettings[colorGroupSchemaItem.name]?.[variantDefinition.name]
        }

        if (variant) {
          if (options.computeValues) {
            themeSettings[colorGroupSchemaItem.name][variantDefinition.name] = resolveColorVariant(
              baseColors[variant.base],
              variant.mix,
            )
          } else {
            themeSettings[colorGroupSchemaItem.name][variantDefinition.name] = variant
          }
        }
      }
    } else {
      // Computed output omits raw color refs; resolveColorRefSettings resolves
      // them into their paired color setting instead.
      if (schemaItem.type === 'colorRef' && options.computeValues) {
        continue
      }

      themeSettings[schemaItem.name] = settingValue ?? getThemeSettingDefaultValueFallback(schemaItem.type)
    }
  }

  // Only the top-level schema contains the color group; within group recursions
  // this is skipped so refs are resolved exactly once, against the full palette.
  const colorGroupSchemaItem = schema.find((item) => item.type === 'colorGroup') as
    | Theme.ColorGroupSettingsSchemaItem
    | undefined
  if (colorGroupSchemaItem) {
    resolveColorRefSettings(
      schema,
      currentSettings,
      defaultSettings,
      themeSettings,
      options,
      colorGroupSchemaItem,
      themeSettings[colorGroupSchemaItem.name],
    )
  }

  return themeSettings
}

// For the currently unused "selection" type, no dedicated default value is defined.
// It could be added here later if needed.
function getThemeSettingDefaultValueFallback(settingType: string) {
  switch (settingType) {
    case 'input':
    case 'customCss':
    case 'radioButtons':
      return ''
    case 'rangeWithInput':
      return 50
    case 'color':
      return 'transparent'
    case 'font':
      return 'sans-serif'
    case 'checkbox':
      return false
    default:
      return null
  }
}

/**
 * Given an Immutable.Map, this function creates a flat object from it,
 * preserving the nesting structure of the individual keys as the key of the
 * resulting flat object.
 */
export function createFlatObjectWithPath(map: ImmutableMap, path = '', delimiter = '-'): Record<string, unknown> {
  return map.reduce((acc: Record<string, unknown>, val, key) => {
    const computedPath = (path ? `${path}${delimiter}` : '') + key

    return typeof val !== 'object' || val === null
      ? { ...acc, [computedPath]: val }
      : { ...acc, ...createFlatObjectWithPath(val, computedPath, delimiter) }
  }, {})
}

/**
 * Replaces `var(--X)` and `var(--X, fallback)` calls in a CSS value expression
 * with their resolved values from `flatVars`. Handles arbitrary nesting, e.g.
 * `var(--X, contrast-color(var(--Y)))`.
 *
 * When `resolveFallbacks` is true, a custom property with no value is replaced by its
 * (recursively resolved) `var()` fallback instead of being left for runtime resolution.
 * Enable it when the result must be a concrete value before further processing (e.g. so
 * `contrast-color()` can be computed).
 */
export function resolveVars(expr: string, flatVars: Record<string, unknown>, resolveFallbacks = false): string {
  const parsed = valueParser(expr)
  const replacements: Array<{ start: number; end: number; value: string }> = []

  parsed.walk((node) => {
    if (node.type !== 'function' || node.value !== 'var') return

    // Strip leading --
    const varName = node.nodes[0].value.slice(2)
    const resolvedValue = flatVars[varName]

    if (resolvedValue !== undefined && typeof resolvedValue !== 'object') {
      replacements.push({ start: node.sourceIndex, end: node.sourceEndIndex, value: String(resolvedValue) })
      // var() is fully replaced, skip descendants
      return false
    }

    if (resolveFallbacks) {
      const separatorIndex = node.nodes.findIndex((child) => child.type === 'div' && child.value === ',')
      if (separatorIndex !== -1) {
        const fallback = valueParser.stringify(node.nodes.slice(separatorIndex + 1)).trim()
        replacements.push({
          start: node.sourceIndex,
          end: node.sourceEndIndex,
          value: resolveVars(fallback, flatVars, resolveFallbacks),
        })
        // var() is fully replaced, skip descendants
        return false
      }
    }

    // Unset: leave the var() for runtime resolution (fallbacks only collapse when resolveFallbacks is set).
  })

  // Process right-to-left to avoid invalidating source positions of later matches.
  // Example: for [{ start: 7, end: 28, value: '#fff' }, { start: 32, end: 51, value: '#000' }],
  // replacing at 7 first shifts position 32, so the second match lands in the wrong range.
  let result = expr
  for (const { start, end, value } of replacements.toReversed()) {
    result = result.slice(0, start) + value + result.slice(end)
  }

  return result
}

const contrastColorProcessor = postcss([postcssContrastColorFunction({ preserve: false })])

/**
 * Builds a CSS `:root { }` block declaring all resolved theme custom properties
 * and pre-computed contrast color values, for injection into SSR HTML and
 * client-side re-renders via ThemeStyleVariables.
 */
export function buildThemeVarsCss(
  themeSettingsSchema: Theme.SettingsSchema,
  defaultThemeSettings: Theme.Settings,
  themeName: string,
  themeSettings: Theme.Settings,
  contrastColorExpressionMap: Record<string, string>,
): string {
  const computed = resolveThemeSettings(
    filterOutCustomCss(themeSettingsSchema),
    themeSettings,
    defaultThemeSettings,
    themeName,
    {
      computeValues: true,
    },
  )
  const flatVars = createFlatObjectWithPath(Immutable.fromJS(computed))

  const varDecls = Object.entries(flatVars)
    .map(([varName, value]) => `--${varName}:${value}`)
    .join(';')

  const contrastDecls = Object.entries(contrastColorExpressionMap)
    // Resolve fallbacks so the contrast input collapses to a concrete color; contrast-color()
    // cannot be computed over a var().
    .map(([key, expr]) => `${key}:${resolveVars(expr, flatVars, true)}`)
    .join(';')

  const themeVarsCss = `:root{${[varDecls, contrastDecls].filter(Boolean).join(';')}}`
  return contrastColorProcessor.process(themeVarsCss, { from: undefined }).css
}

const SOURCE_MAPPING_URL_RE = /\/\*# sourceMappingURL=(?:.*?)\*\//

/**
 * Returns processed CSS with the `sourceMappingURL` pragma that PostCSS strips re-appended.
 */
export function withSourceMapURL(rawCss: string, processedCss: string): string {
  const sourceMapLink = SOURCE_MAPPING_URL_RE.exec(rawCss)?.[0]
  return sourceMapLink ? `${processedCss}\n${sourceMapLink}` : processedCss
}

function filterOutCustomCss(schema: Theme.SettingsSchemaItem[]): Theme.SettingsSchemaItem[] {
  return schema
    .filter((item) => item.type !== 'customCss')
    .map((item) =>
      item.type === 'group'
        ? { ...item, settings: filterOutCustomCss((item as Theme.GroupSettingsSchemaItem).settings) }
        : item,
    )
}
